<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import useDebouncedFunction from '@/composables/useDebouncedFuncion'
import apiClient from '@/apiClient.js'
import { useAuthStore } from '@/stores/AuthStore'
import { isSearchEndpointAllowed } from '@/utils/searchAccess'
const props = defineProps({
  'submitSearch': { type: Boolean, default: false },
  'output': { required: true },
  'url': { required: true },
  'placeholder': {},
  'modelValue': {},
  'canAdd': {},
  'addOnlyWhenEmpty': { default: false },
  'addLabel': { default: 'Add' },
  'clearOnSelect': { default: true },
  'autoShowResults': { default: true },
  'searchOnFocus': { default: false },
  'maxQueryLength': { default: 64 },
  'inputClass': { default: '' },
  'resultsClass': { default: '' },
})
const emit = defineEmits(['selected', 'update:modelValue', 'empty', 'found', 'submit'])
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const searchQueryLocal = ref(props.modelValue ?? '')
const searchResults = ref()
const showResults = ref(props.autoShowResults !== false)
const searchResultElements = ref([])
const loadingResults = ref(false)
const selectedItemIndex = ref(-1)
const selectedLabel = ref(null)
let searchVersion = 0
let lastRequestedVersion = -1
let latestRequestId = 0
let searchController = null
const authStore = useAuthStore()
const canSearch = computed(() =>
  isSearchEndpointAllowed(props.url, authStore.roles)
)
const showAddButton = computed(() => {
  if (!props.canAdd) return false
  if (!props.addOnlyWhenEmpty) return true

  return Array.isArray(searchResults.value) && searchResults.value.length === 0
})

const addBtn = () => {
  showResults.value = false
  emit('empty', searchQueryLocal.value)
}

const { debouncedFunction: search, disableTimer } = useDebouncedFunction((version) => {
  if (version === lastRequestedVersion) return
  lastRequestedVersion = version

  const requestId = ++latestRequestId
  loadingResults.value = false
  const maxQueryLength = Math.max(2, Number(props.maxQueryLength) || 64)
  const requestQuery = String(searchQueryLocal.value ?? '').trim().slice(0, maxQueryLength)
  if (requestQuery.length < 2) {
    searchResults.value = null
    return
  }
  if (!canSearch.value) {
    searchResults.value = null
    showResults.value = false
    return
  }
  searchController?.abort()
  searchController = new AbortController()
  loadingResults.value = true
  apiClient
    .get(props.url + encodeURIComponent(requestQuery), {
      suppressErrorAlert: true,
      signal: searchController.signal,
    })
    .then(r => {
      if (requestId !== latestRequestId) return
      loadingResults.value = false

      // no results found
      if (
        r.data &&
        typeof r.data.length !== 'undefined' &&
        r.data.length === 0
      ) {
        searchResults.value = []
        showResults.value = Boolean(props.canAdd)
        emit('update:modelValue', searchQueryLocal.value)
      }
      // display results
      else {
        searchResults.value = r.data
        showResults.value = true
        selectedItemIndex.value = -1
        emit('found', searchResults.value)
      }
    })
    .catch(() => {
      if (requestId !== latestRequestId) return
      loadingResults.value = false
      searchResults.value = null
      showResults.value = false
    })
})

function handleInput(e) {
  searchController?.abort()
  latestRequestId += 1
  selectedItemIndex.value = -1
  const nextValue = e.target.value
  searchQueryLocal.value = nextValue
  if (nextValue !== selectedLabel.value) {
    selectedLabel.value = null
  }
  emit('update:modelValue', nextValue)
  searchVersion += 1
  search(searchVersion)
}

function handleFocus() {
  if (
    props.searchOnFocus
    && searchQueryLocal.value.length >= 2
    && searchQueryLocal.value !== selectedLabel.value
  ) {
    searchVersion += 1
    search(searchVersion)
  }
}

function handleKeyDown(e) {
  if ((e.key === 'Enter' || e.keyCode === 13) && props.submitSearch && !e.isComposing) {
    e.preventDefault()
    const query = String(searchQueryLocal.value ?? '').trim().slice(0, props.maxQueryLength)
    if (query.length < 2 || !canSearch.value) return
    disableTimer()
    searchController?.abort()
    latestRequestId += 1
    loadingResults.value = false
    showResults.value = false
    emit('submit', query)
    return
  }
  if (!searchResults.value || !searchResults.value.length) return

  if (e.keyCode === 40) {
    e.preventDefault()
    selectedItemIndex.value = Math.min(
      selectedItemIndex.value + 1,
      searchResults.value.length - 1
    )
    return
  }

  if (e.keyCode === 38) {
    e.preventDefault()
    selectedItemIndex.value = Math.max(selectedItemIndex.value - 1, 0)
    return
  }

  if (e.keyCode === 13) {
    e.preventDefault()
    const index =
      selectedItemIndex.value >= 0 ? selectedItemIndex.value : 0
    selected(searchResults.value[index])
  }
}

function selected(val) {
  searchController?.abort()
  disableTimer() // Cancel the debounced function to disable results popping up again
  latestRequestId += 1
  loadingResults.value = false
  showResults.value = false
  emit('selected', val)

  if (props.clearOnSelect) {
    searchQueryLocal.value = ''
    selectedLabel.value = null
    emit('update:modelValue', '')
  } else {
    const label = props.output ? props.output(val) : ''
    searchQueryLocal.value = label
    selectedLabel.value = label
    emit('update:modelValue', label)
  }
}

watch(selectedItemIndex, async (index) => {
  if (index < 0) return
  await nextTick()
  const el = searchResultElements.value[index]
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ block: 'nearest' })
  }
})

watch(searchResults, () => {
  selectedItemIndex.value = -1
  searchResultElements.value = []
})

watch(
  () => props.modelValue,
  (val) => {
    if (typeof val === 'string' && val !== searchQueryLocal.value) {
      searchQueryLocal.value = val
    }
  }
)

onBeforeUnmount(() => {
  searchController?.abort()
  disableTimer()
  latestRequestId += 1
})
</script>
<template>
  <div class="relative">
    <input type="text" v-model="searchQueryLocal" @keydown="handleKeyDown" @focus="handleFocus"
      @input="handleInput"
      :class="['input w-full bg-white text-slate-900 border-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700 dark:placeholder:text-slate-500', props.inputClass || 'px-4 py-2']"
      autocomplete="off" :placeholder="props.placeholder" />
    <div v-if="showResults && searchQueryLocal"
      :class="['absolute left-0 right-0 z-50 mt-2 max-h-36 overflow-y-auto rounded-md border border-gray-300 bg-white text-slate-900 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100', props.resultsClass]">
      <button v-if="showAddButton" type="button"
        class="block w-full p-2 text-left hover:cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-800"
        @mousedown.prevent
        @click.prevent="addBtn">
        + {{ props.addLabel }} <template>{{ searchQueryLocal }}</template>
      </button>
      <div v-for="(item, i) in searchResults" :key="item" :ref="el => (searchResultElements[i] = el)"
        @mousedown.prevent
        @click="selected(item)" :class="{
        'p-2 hover:bg-slate-300 hover:cursor-pointer dark:hover:bg-slate-800': true,
        'bg-slate-200 dark:bg-slate-800/80': selectedItemIndex === i,
      }">
        <template v-if="props.output(item)">{{ props.output(item) }}</template>
      </div>
    </div>
    <div v-if="loadingResults"
      class="absolute left-0 right-0 z-50 mt-2 max-h-36 overflow-y-auto rounded-md border border-gray-300 bg-white text-slate-900 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <loading-spinner></loading-spinner>
    </div>
  </div>
</template>
